import isEmpty from 'lodash.isempty';
import type { AppliedIncentives, IPencilOfferInputsIncentive } from '@makemydeal/dr-platform-types';
import { CASH } from '@makemydeal/dr-platform-types';
import * as selectors from '../selectors';
import type { CashTransformed } from '../types/paymentTypes';
import {
    isCashDealIncentivesFixesEnabled,
    isCashSwitchStandardRebateEnabled,
    parseIncentiveId,
    updateTermIncentives
} from './incentives';

// The orchestrator's cash incentive entries (IOrchestratorIncentive: incentiveId/cash/programName/...)
// use different field names than the internal id/amount catalog shape. Mapping them across and running
// the result through the same updateTermIncentives the program-catalog path uses (overrideHandlers.ts's
// UPDATE_CASH_AVAILABLE_INCENTIVES branch) keeps both writers of byOfferType.cash.incentives producing
// the same rich shape (exclusions/name/type/isStandardIncentive), so a payment response can't silently
// replace a richer, program-sourced catalog with a thinner one
export const formatPencilInputCashIncentives = (incentives: any, conditionType: any): IPencilOfferInputsIncentive[] => {
    if (isEmpty(incentives) || isEmpty(conditionType)) {
        return [];
    }
    const mapped = incentives.map((incentive: any) => ({
        id: incentive.incentiveId ?? incentive.id,
        amount: incentive.cash ?? incentive.amount,
        exclusions: incentive.exclusions,
        name: incentive.programName ?? incentive.name,
        program: incentive.programId,
        type: incentive.incentiveType,
        isStandardIncentive: incentive.isStandardIncentive,
        description: incentive.description
    }));

    return updateTermIncentives(mapped, `${conditionType}_${CASH}_`, true) as IPencilOfferInputsIncentive[];
};

// A recalc triggered by toggling an incentive's checkbox (UPDATE_SELECTED_INCENTIVES) isn't a
// "program catalog" action, so includeIncentives resolves to false and the response's
// appliedIncentives.conditionals/nonConditionals breakdown comes back empty even though the same
// response's flat appliedIncentiveIds correctly lists what was just applied - the display total
// (getAppliedIncentivesTotalForDisplay) has no term to fall back on for cash, so it depends entirely
// on that breakdown, and shows $0 for a genuinely-applied incentive without this. Rebuilds the
// id-string breakdown by cross-referencing the reliable appliedIncentiveIds against the (already
// catalog-protected) incentives list, instead of trusting the response's own breakdown field.
// See DE759607/US2076398.
export const deriveAppliedIncentivesFromIds = (
    appliedIncentiveIds: number[],
    catalog: { conditionals: IPencilOfferInputsIncentive[]; nonConditionals: IPencilOfferInputsIncentive[] }
): AppliedIncentives => {
    const appliedIdSet = new Set(appliedIncentiveIds);
    const matchIds = (bucket: IPencilOfferInputsIncentive[]): string[] =>
        bucket.filter((incentive) => appliedIdSet.has(parseIncentiveId(incentive.id))).map((incentive) => incentive.id);

    return {
        conditionals: matchIds(catalog.conditionals),
        nonConditionals: matchIds(catalog.nonConditionals)
    };
};

type CashIncentivesPayload = { cash: Pick<CashTransformed, 'incentives' | 'appliedIncentives' | 'appliedIncentiveIds'> };

const readCashCatalogGroups = (extendedOffer: any, incentives: CashTransformed['incentives'] | undefined) => {
    if (isCashSwitchStandardRebateEnabled(extendedOffer) || !isCashDealIncentivesFixesEnabled(extendedOffer)) {
        return { conditional: incentives?.conditionals, nonConditional: incentives?.nonConditionals };
    }
    return { conditional: undefined, nonConditional: undefined };
};

export const updateOfferWithCashIncentives = (extendedOffer: any, payload: CashIncentivesPayload) => {
    const catalogGroups = readCashCatalogGroups(extendedOffer, payload.cash.incentives);
    const conditionals = formatPencilInputCashIncentives(catalogGroups.conditional, 'conditional');
    const nonConditionals = formatPencilInputCashIncentives(catalogGroups.nonConditional, 'nonconditional');

    const appliedIncentives: AppliedIncentives | undefined = payload.cash.appliedIncentives;
    const appliedIncentiveIds: number[] = payload.cash.appliedIncentiveIds || [];
    const totalIncentives = selectors.findAllIncentiveTotalFromOffer(extendedOffer, CASH) || 0;

    const cashIncentives = {
        incentives: {
            nonConditionals,
            conditionals
        },
        appliedIncentives,
        appliedIncentiveIds,
        totalIncentives
    };
    return cashIncentives;
};
